import { useState } from 'react'
import clsx from 'clsx'
import {
  IMAGE_SIZES,
  imageSource,
  imageStatusMarker,
  linkRuns,
  safeLinkUrl,
  textParagraphs,
  type ImageSource,
} from 'lib/community/discussions'
import { safeImageUrl } from 'lib/community/model'
import type { DiscussionBlock, DiscussionImage } from 'lib/community/discussions-types'
import Lightbox, { type LightboxImage } from './Lightbox'
import styles from './DiscussionThread.module.css'

/** The links a reader wrote: never endorsed, never followed by a crawler, no opener. */
const UGC_REL = 'ugc nofollow noopener'

/** A text block: paragraphs on blank lines, single line breaks kept, http(s) URLs linked. */
const TextBlock: React.VFC<{ body: string }> = ({ body }) => (
  <div className={styles.text}>
    {textParagraphs(body).map((paragraph, i) => (
      // eslint-disable-next-line react/no-array-index-key -- a fixed body; paragraphs may repeat
      <p key={i}>
        {linkRuns(paragraph).map((run, j) => {
          if (!('href' in run)) {
            // eslint-disable-next-line react/no-array-index-key
            return <span key={j}>{run.text}</span>
          }
          return (
            // eslint-disable-next-line react/no-array-index-key
            <a key={j} href={run.href} rel={UGC_REL} target="_blank">
              {run.text}
            </a>
          )
        })}
      </p>
    ))}
  </div>
)

/** A photo of a set: the lightbox's full size, and the mosaic's `card` size with `full` in its
 *  srcset when DealHunt sent `variants` (plain `url` otherwise). */
export interface SetImage extends LightboxImage {
  shown: ImageSource
}

/** The https images of a list, with their alt text (`altFallback` when an image has none) and —
 *  on a discussion author's own view only — their status marker; anything else is not drawn.
 *  Shared with an owner review's photos (`components/owner-notes/ReviewPhotos`). */
export function toSetImages(
  images: readonly Pick<
    DiscussionImage,
    'url' | 'width' | 'height' | 'alt' | 'variants' | 'status'
  >[],
  altFallback = ''
): SetImage[] {
  return images.flatMap(image => {
    const url = safeImageUrl(image?.url)
    const shown = imageSource(image, 'card', 'full')
    return url && shown
      ? [
          {
            url,
            alt: image.alt ?? altFallback,
            width: image.width,
            height: image.height,
            shown,
            marker: imageStatusMarker(image.status),
          },
        ]
      : []
  })
}

function setImages(block: Extract<DiscussionBlock, { type: 'images' }>): SetImage[] {
  return toSetImages(Array.isArray(block.images) ? block.images : [])
}

/** `sizes` for photo `i` of a set shown `count` at a time. */
function photoSizes(lead: boolean, i: number, count: number): string {
  if (!lead) {
    return IMAGE_SIZES.grid
  }
  if (count === 1) {
    return IMAGE_SIZES.mosaicSolo
  }
  if (count === 2) {
    return IMAGE_SIZES.mosaicPair
  }
  return i === 0 ? IMAGE_SIZES.mosaicLead : IMAGE_SIZES.mosaicSmall
}

interface ImageSetProps {
  images: SetImage[]
  caption?: string
  /** The post's first set: the mosaic (one large, two small, "+N"); later sets are a grid. */
  lead: boolean
  onOpen: (index: number) => void
}

/** One photo set: the lead mosaic (one large, two small, "+N") or a grid. Also an owner
 *  review's photos on its thread (`ReviewPhotos` layout `mosaic`). */
export const ImageSet: React.VFC<ImageSetProps> = ({ images, caption, lead, onOpen }) => {
  const shown = lead ? images.slice(0, 3) : images
  const rest = lead ? images.length - shown.length : 0
  return (
    <figure className={styles.figure}>
      <div
        className={clsx(lead ? styles.mosaic : styles.grid, {
          [styles.mosaicOne]: lead && shown.length === 1,
          [styles.mosaicTwo]: lead && shown.length === 2,
        })}
      >
        {shown.map((image, i) => (
          <button
            // eslint-disable-next-line react/no-array-index-key -- the same photo may be in a set twice
            key={`${image.url}-${i}`}
            type="button"
            className={clsx(styles.photo, {
              [styles.mosaicLead]: lead && i === 0,
              [styles.photoHidden]: image.marker?.tone === 'hidden',
            })}
            aria-label={`Open photo ${i + 1} of ${images.length}${
              image.alt ? `: ${image.alt}` : ''
            }${image.marker ? ` (${image.marker.label})` : ''}`}
            onClick={() => onOpen(i)}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={image.shown.src}
              srcSet={image.shown.srcSet}
              sizes={image.shown.srcSet ? photoSizes(lead, i, shown.length) : undefined}
              alt={image.alt}
              width={image.shown.width}
              height={image.shown.height}
              // The thread's lead photo is its largest paint: fetched at once, never lazily.
              loading={lead && i === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
            {i === shown.length - 1 && rest > 0 && (
              <span className={styles.photoMore}>{`+${rest}`}</span>
            )}
            {image.marker && (
              // Above "+N" (a corner, not the centre), so neither covers the other.
              <span className={clsx(styles.statusBadge, styles.photoBadge)} aria-hidden="true">
                {image.marker.label}
              </span>
            )}
          </button>
        ))}
      </div>
      {caption && <figcaption className={styles.caption}>{caption}</figcaption>}
    </figure>
  )
}

/** A link block: a preview card with the domain and the writer's title (no Open Graph fetch in
 *  v1). https only; anything else is not drawn. */
const LinkBlock: React.VFC<{ block: Extract<DiscussionBlock, { type: 'link' }> }> = ({ block }) => {
  const url = safeLinkUrl(block.url)
  if (!url) {
    return null
  }
  const parsed = new URL(url)
  const domain = (block.domain || parsed.hostname).replace(/^www\./, '')
  const title = block.title?.trim() || `${parsed.hostname}${parsed.pathname}`.replace(/\/$/, '')
  return (
    <a className={styles.linkCard} href={url} rel={UGC_REL} target="_blank">
      <span className={styles.linkDomain}>{domain}</span>
      <span className={styles.linkTitle}>{title}</span>
    </a>
  )
}

/**
 * A discussion's body (design 10c): the blocks in the order they were written — text, headings,
 * photo sets (the first as the mosaic, later ones as a grid, each with its caption, every photo
 * opening the lightbox) and link cards. Every link the reader wrote is `rel="ugc nofollow
 * noopener"`.
 */
const Blocks: React.VFC<{ blocks: DiscussionBlock[] }> = ({ blocks }) => {
  const [open, setOpen] = useState<{ images: LightboxImage[]; index: number } | null>(null)
  const lead = blocks.findIndex(b => b.type === 'images' && setImages(b).length > 0)
  return (
    <div className={styles.blocks}>
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'text':
            // eslint-disable-next-line react/no-array-index-key -- blocks are a fixed, ordered list
            return <TextBlock key={i} body={block.body} />
          case 'heading':
            return (
              // eslint-disable-next-line react/no-array-index-key
              <h2 key={i} className={styles.heading}>
                {block.text}
              </h2>
            )
          case 'images': {
            const images = setImages(block)
            return images.length ? (
              <ImageSet
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                images={images}
                caption={block.caption}
                lead={i === lead}
                onOpen={index => setOpen({ images, index })}
              />
            ) : null
          }
          case 'link':
            // eslint-disable-next-line react/no-array-index-key
            return <LinkBlock key={i} block={block} />
          default:
            return null
        }
      })}
      {open && <Lightbox images={open.images} start={open.index} onClose={() => setOpen(null)} />}
    </div>
  )
}

export default Blocks
